import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { getMediaProviders } from "../../services/mediaLibraryService";
import MediaLibraryTabs from "./MediaLibraryTabs";
import RecentUploadsTab from "./RecentUploadsTab";
import UploadDeviceTab from "./UploadDeviceTab";
import ProviderSearchTab from "./ProviderSearchTab";
import "./media-library.css";

const RECENT_TAB = {
  key: "recent",
  label: "Recent Uploads",
  icon: "ri-history-line",
};
const UPLOAD_TAB = {
  key: "upload",
  label: "Upload Device",
  icon: "ri-upload-cloud-2-line",
};

const PROVIDER_ICONS = {
  pexels: "ri-camera-lens-line",
  pixabay: "ri-image-line",
  unsplash: "ri-sun-line",
  freepik: "ri-palette-line",
};

const extractProviders = (res) => {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  return data?.result || data?.data || data?.providers || [];
};

const normalizeProvider = (raw) => {
  // Provider key drives API calls; displayName is the UI label.
  const providerKey =
    typeof raw === "string"
      ? raw
      : raw?.provider ||
        raw?.providerType ||
        raw?.name ||
        raw?.code ||
        "";
  if (!providerKey) return null;

  const key = String(providerKey).toLowerCase().trim();
  // "upload" already exists as the fixed "Upload Device" tab.
  if (key === "upload") return null;

  const displayName =
    (typeof raw === "object" && (raw?.displayName || raw?.label)) ||
    key.charAt(0).toUpperCase() + key.slice(1);

  return {
    key,
    label: displayName,
    icon: PROVIDER_ICONS[key] || "ri-global-line",
  };
};

/**
 * Centralized, reusable Media Library picker.
 *
 * Usage (later):
 *   <MediaLibraryModal show={open} onClose={...} onSelect={(media) => ...} />
 *
 * onSelect receives:
 *   { mediaAssetId, mediaUrl, thumbnailUrl, mediaName, providerType }
 */
const MediaLibraryModal = ({
  show,
  onClose,
  onSelect,
  title = "Media Library",
  subtitle = "Upload, Search and Reuse Media Assets",
  defaultKeyword = "",
  onImportForCrop,
  refreshKey: externalRefreshKey = 0,
}) => {
  const [activeTab, setActiveTab] = useState("recent");
  const [providerTabs, setProviderTabs] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [highlightId, setHighlightId] = useState(null);

  const recentRefreshKey = refreshKey + externalRefreshKey;

  const fetchProviders = useCallback(async () => {
    try {
      const response = await getMediaProviders();
      const providers = extractProviders(response)
        .map(normalizeProvider)
        .filter(Boolean)
        .filter((p, i, arr) => arr.findIndex((x) => x.key === p.key) === i);
      setProviderTabs(providers);
    } catch {
      setProviderTabs([]);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setActiveTab("recent");
      setHighlightId(null);
      setRefreshKey((k) => k + 1);
      fetchProviders();
    }
  }, [show, fetchProviders]);

  const tabs = useMemo(
    () => [RECENT_TAB, ...providerTabs, UPLOAD_TAB],
    [providerTabs]
  );

  const handleSelect = useCallback(
    (media) => {
      if (!media) return;
      onSelect?.({
        mediaAssetId: media.mediaAssetId,
        mediaUrl: media.mediaUrl,
        thumbnailUrl: media.thumbnailUrl,
        mediaName: media.mediaName,
        providerType: media.providerType,
      });
      onClose?.();
    },
    [onSelect, onClose]
  );

  const handleUploaded = useCallback(
    (media) => {
      setRefreshKey((k) => k + 1);
      if (media) {
        setHighlightId(media.mediaAssetId ?? null);
        handleSelect(media);
        return;
      }
      setHighlightId(null);
      setActiveTab("recent");
    },
    [handleSelect]
  );

  const renderActiveTab = () => {
    if (activeTab === "recent") {
      return (
        <RecentUploadsTab
          show={show}
          refreshKey={recentRefreshKey}
          highlightId={highlightId}
          onSelect={handleSelect}
        />
      );
    }
    if (activeTab === "upload") {
      return <UploadDeviceTab onUploaded={handleUploaded} />;
    }
    const provider = providerTabs.find((p) => p.key === activeTab);
    return (
      <ProviderSearchTab
        key={provider?.key || activeTab}
        provider={provider?.key || activeTab}
        providerName={provider?.label || "Provider"}
        defaultKeyword={defaultKeyword}
        onSelect={handleSelect}
        onImportForCrop={onImportForCrop}
      />
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      scrollable
      dialogClassName="xc-ml-modal"
    >
      <Modal.Header closeButton>
        <div>
          <h5 className="xc-ml-header__title">
            <i className="ri-folder-image-line" aria-hidden="true" />
            {title}
          </h5>
          <p className="xc-ml-header__subtitle">{subtitle}</p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <MediaLibraryTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
        {renderActiveTab()}
      </Modal.Body>
    </Modal>
  );
};

export default MediaLibraryModal;
